import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Group,
  Menu,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconChecklist,
  IconClipboardCheck,
  IconClipboardList,
  IconCreditCard,
  IconCurrencyDollar,
  IconCalendarEvent,
  IconCalendarPlus,
  IconDashboard,
  IconFileInvoice,
  IconFileText,
  IconInbox,
  IconLogout,
  IconMessage,
  IconPlus,
  IconSchool,
  IconSettings,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconBuildingHospital,
  IconSwitchHorizontal,
  IconChartBar,
  IconBug,
  IconMessageCircle,
} from '@tabler/icons-react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NotificationsPopover } from './components/NotificationsPopover';
import { InAppNotificationToastListener } from './components/InAppNotificationToastListener';
import { useInAppNotifications } from './hooks/useInAppNotifications';
import { FhirRealtimeProvider } from './hooks/useFhirRealtime';
import { AccessDenied } from './components/AccessDenied';
import { RequireClientRole } from './components/RequireClientRole';

import { HudClientSearch } from './components/HudClientSearch';
import { Loading } from './components/Loading';
import { RecentlyViewedNavSection } from './components/RecentlyViewedNavSection';
import { UnifyNavLink } from './components/UnifyNavLink';
import { ClientsProvider } from './contexts/ClientsContext';
import { RecentlyViewedClientsProvider } from './contexts/RecentlyViewedClientsContext';
import { usePermissions } from './contexts/PermissionContext';
import { usePortalSubject } from './contexts/PortalSubjectContext';
import {
  AiAssistantLayout,
  AiAssistantProvider,
  FreudLauncher,
} from './features/ai-assistant';
import { useApiInitialization } from './hooks';
import {
  clientPortalNavItemAllowed,
  defaultClientPortalHome,
  getEffectiveClientPortalPermissions,
  isClientPortalPathAllowed,
} from './lib/clientPortalNavPermissions';
import {
  type DisplayUserRole,
  getEffectiveDisplayUserRole,
} from './lib/displayUserRole';
import { collapseRolesForDisplay } from '@unify/fhir-model/role-display';
import { ROLE_LABELS } from './lib/rbacRoles';
import { profileAccountDisplayName } from './lib/profileAccountDisplayName';
import { useBugReport } from './hooks/useBugReport';
import { useProductFeedback } from './hooks/useProductFeedback';
import { BugReportModal } from './components/BugReportModal';
import { FeedbackModal } from './components/FeedbackModal';
import {
  canAccessEnrollments,
  canSelectEnrollmentPractitioner,
} from './lib/enrollmentAccess';
import { USER_ROLE_STORAGE_KEY } from './lib/userProfileRouteAccess';
import { isAuthPath, shouldBlockRenderWhileLoading } from './lib/appRouteGate';
import { isInteractiveUserProfile } from './lib/isInteractiveMedplumProfile';
import { usePreferredDisplayTimeZone } from './hooks/usePreferredDisplayTimeZone';
import { ACCOUNT_STATUS_TAG_SYSTEM, PENDING_ACTIVATION_TAG_CODE } from '@unify/fhir-model/constants';

import { AuthCallback } from './pages/auth/AuthCallback';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { SetPasswordPage } from './pages/auth/SetPasswordPage';
import { SignInPage } from './pages/auth/SignInPage';
import { PatientDetailPage } from './pages/patients/PatientDetailPage';
import { PatientListPage } from './pages/patients/PatientListPage';
import { ClientsScreen } from './pages/caseload/ClientsScreen';
import { AssessmentResponsePage } from './pages/clients/AssessmentResponsePage';
import { ChartAdminNoteRoute } from './pages/clients/ChartAdminNoteRoute';
import { ChartDocumentPage } from './pages/clients/ChartDocumentPage';
import { ClientChartPage } from './pages/clients/ClientChartPage';
import { ClientProfilePage } from './pages/clients/ClientProfilePage';
import { DischargeSummaryPage } from './pages/clients/DischargeSummaryPage';
import { EpisodeOfCarePage } from './pages/clients/EpisodeOfCarePage';
import { MedplumDirectoryUsersLayout } from './pages/settings/MedplumDirectoryUsersLayout';
import { SettingsPage } from './pages/settings/SettingsPage';

const DesignSystemRoutes = lazy(async () => {
  const m = await import('./pages/design-system/DesignSystemRoutes');
  return { default: m.DesignSystemRoutes };
});
const AnalyticsRoutes = lazy(async () => {
  const m = await import('./pages/analytics/AnalyticsRoutes');
  return { default: m.AnalyticsRoutes };
});
const EnrollmentPdfRunPage = lazy(async () => {
  const m = await import('./features/enrollment-pdf');
  return { default: m.EnrollmentPdfRunPage };
});
import { UserSettingsPage } from './pages/settings/UserSettingsPage';
import { TemplateEditorPage } from './pages/settings/template-library/TemplateEditorPage';
import { MessagingPage } from './pages/messaging';
import { TasksPage } from './pages/tasks';
import { SupervisionDashboardPage, DocumentReviewPage, SupervisionMeetingFormPage } from './pages/supervision';
import { TrainingsPage } from './pages/trainings';
import { EnrollmentsPage } from './pages/enrollments';
import { PracticesEnrollmentsPage } from './pages/practices';
import { GroupsPage, GroupDetailRoute } from './pages/groups';
import { FinancesPage } from './pages/finances/FinancesPage';
import { InvoiceDetailPage } from './pages/finances/InvoiceDetailPage';
import { PaymentDetailPage } from './pages/finances/PaymentDetailPage';
import { PracticeFinancesPage } from './pages/finances/PracticeFinancesPage';
import { ProviderCalendarPage } from './pages/calendar';
import {
  AcknowledgmentsPage,
  AccountsPage,
  BatchRunsPage,
  BillingClaimsPage,
  BillingIndexPage,
  BillingSectionLayout,
  ClaimDetailPage,
  Cms1500ClaimPage,
  EobDetailPage,
  OpsQueuePage,
  RemittancesPage,
} from './pages/billing';
import { ClientIntakeDirectoryPage, ClientIntakePage } from './pages/client-intake';
import { HomeTilesPage } from './pages/dashboard-home';
import { AppointmentPage } from './pages/appointments';
import { TelehealthSessionPage } from './pages/session';
import { ZoomCallPage } from './pages/session/ZoomCallPage';
import { ZoomClosedPage } from './pages/session/ZoomClosedPage';
import { InPersonRecordingPage } from './pages/appointments/InPersonRecordingPage';
import {
  AssessmentFillPage,
  ClientPortalBillingPage,
  ClientPortalDashboardPage,
  ClientPortalDirectoryPage,
  ClientPortalDocumentsPage,
  ClientPortalIntakePage,
  ClientPortalMessagesPage,
  ClientPortalRequestServicePage,
  ClientPortalRequestStatusPage,
  ClientTelehealthJoinPage,
} from './pages/client-portal';
import { useUnreadThreadCount } from './pages/messaging/hooks';
import { EpisodeTrackerPage } from './pages/episode-tracker';
import { ReferralRequestsPage } from './pages/referral-requests';
import { OnboardingHubPage } from './pages/onboarding/OnboardingHubPage';
import { OnboardingLayout } from './pages/onboarding/OnboardingLayout';
import { useOnboardingStatus } from './pages/onboarding/useOnboardingStatus';
import { DoneSection } from './pages/onboarding/sections/DoneSection';
import { PersonalInfoSection } from './pages/onboarding/sections/PersonalInfoSection';
import { AvailabilitySection } from './pages/onboarding/sections/AvailabilitySection';
import { ProfileSection } from './pages/onboarding/sections/ProfileSection';
import { LicensesEducationSection } from './pages/onboarding/sections/LicensesEducationSection';
import { AttestationSection } from './pages/onboarding/sections/AttestationSection';
import { FEATURE_VISIBILITY, isClientPortalFeatureVisible, isStaffFeaturePathVisible } from './config/featureVisibility';

/**
 * Navigation items for the sidebar
 */
interface NavItem {
  icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  label: string;
  href: string;
  badge?: number;
}

const userRoleOptions: Array<{ value: DisplayUserRole; label: string }> = [
  { value: 'Unify Admin', label: 'Unify Admin' },
  { value: 'Client', label: 'Client' },
  { value: 'Provider', label: 'Provider' },
  { value: 'Practice Owner', label: 'Practice Owner' },
  { value: 'Practice Admin', label: 'Practice Admin' },
];

const dashboardNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/dashboard' },
];

const tasksMessagesNavItems: NavItem[] = [
  { icon: IconChecklist, label: 'Tasks', href: '/dashboard/tasks' },
  ...(FEATURE_VISIBILITY.staffMessages
    ? [{ icon: IconMessage, label: 'Messages', href: '/dashboard/messages' }]
    : []),
];

const mainNavItems: NavItem[] = [...dashboardNavItems, ...tasksMessagesNavItems];

/**
 * Client portal section (client-facing screens).
 * Badge counts are applied dynamically inside the component via useMemo.
 */
const clientPortalNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/client-portal' },
  { icon: IconFileText, label: 'Documents', href: '/client-portal/documents' },
  { icon: IconCreditCard, label: 'Billing', href: '/client-portal/billing' },
  { icon: IconMessage, label: 'Messages', href: '/client-portal/messages' },
];

/**
 * Admin section navigation items (Unify Admin)
 */
const adminNavItems: NavItem[] = FEATURE_VISIBILITY.finances
  ? [{ icon: IconCurrencyDollar, label: 'Finances', href: '/dashboard/practice-finances' }]
  : [];

/**
 * Practices section navigation items (payer enrollments, billing relationships)
 */
const practicesNavItems: NavItem[] = FEATURE_VISIBILITY.practiceEnrollments
  ? [{ icon: IconBuildingHospital, label: 'Practice Enrollments', href: '/dashboard/practices/enrollments' }]
  : [];

const enrollmentsNavItems: NavItem[] = [
  { icon: IconClipboardList, label: 'Enrollments', href: '/dashboard/enrollments' },
];

/**
 * Practice Owner only – Finances
 */
const financesNavItems: NavItem[] = FEATURE_VISIBILITY.finances
  ? [{ icon: IconCurrencyDollar, label: 'Finances', href: '/dashboard/finances' }]
  : [];

/**
 * Practice Owner / Practice Admin – Groups (therapy groups management)
 */
const groupsNavItems: NavItem[] = [
  { icon: IconUsersGroup, label: 'Groups', href: '/dashboard/groups' },
];

/**
 * Provider section – complete nav items in display order
 */
const providerNavItems: NavItem[] = [
  { icon: IconDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: IconCalendarEvent, label: 'Calendar', href: '/dashboard/calendar' },
  { icon: IconClipboardList, label: 'Caseload', href: '/dashboard/caseload' },
  { icon: IconChecklist, label: 'Tasks', href: '/dashboard/tasks' },
  ...(FEATURE_VISIBILITY.staffMessages
    ? [{ icon: IconMessage, label: 'Messages', href: '/dashboard/messages' }]
    : []),
  { icon: IconClipboardCheck, label: 'Supervision', href: '/dashboard/supervision' },
  ...(FEATURE_VISIBILITY.trainings
    ? [{ icon: IconSchool, label: 'Trainings', href: '/dashboard/trainings' }]
    : []),
  ...(FEATURE_VISIBILITY.enrollments ? enrollmentsNavItems : []),
];

/**
 * Settings – visible to all roles
 */
const settingsNavItems: NavItem[] = [{ icon: IconSettings, label: 'Settings', href: '/dashboard/settings' }];

function isNavActive(href: string, pathname: string): boolean {
  // Prevent `/dashboard` from appearing "active" for all `/dashboard/*` routes,
  // which visually makes other items look nested under it.
  if (href === '/dashboard') {
    return pathname === '/dashboard' || pathname === '/dashboard/home';
  }
  // Same for client portal root: keep "Portal dashboard" active only on its exact route.
  if (href === '/client-portal') {
    return pathname === '/client-portal';
  }
  return pathname === href || pathname.startsWith(href + '/');
}

/** Redirect legacy /set-password/:id/:secret path-based URLs to /auth/set-password?id=…&secret=… */
function LegacySetPasswordRedirect(): JSX.Element {
  const { id, secret } = useParams<{ id: string; secret: string }>();
  const to = `/auth/set-password?id=${encodeURIComponent(id ?? '')}&secret=${encodeURIComponent(secret ?? '')}`;
  return <Navigate to={to} replace />;
}

/** Redirect /auth/setpassword (no hyphen) to /auth/set-password, preserving query params */
function SetPasswordAliasRedirect(): JSX.Element {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={`/auth/set-password${qs ? `?${qs}` : ''}`} replace />;
}

/**
 * Main App component
 * Handles routing and layout based on authentication state
 */
export function App(): JSX.Element {
  return (
    <FhirRealtimeProvider>
      <InAppNotificationToastListener />
      <AppContent />
    </FhirRealtimeProvider>
  );
}

function AppContent(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  usePreferredDisplayTimeZone(profile);
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, { toggle, close }] = useDisclosure();
  const [accountMenuOpened, setAccountMenuOpened] = useState(false);
  const bugReport = useBugReport();
  const productFeedback = useProductFeedback();
  // True when the viewport is narrower than Mantine's 'sm' breakpoint (~768px).
  // Used to adapt the header layout for mobile screens.
  const isMobileViewport = useMediaQuery('(max-width: 48em)') ?? false;

  // Medplum-backed permission state (authoritative in live mode)
  const rbac = usePermissions();

  // Portal subject context for multi-account client portal switching
  const portalSubject = usePortalSubject();
  const isViewingRelatedClient = portalSubject.activeAccount !== null && !portalSubject.activeAccount.isSelf;

  // Dev role toggle: authoritative in mock Medplum; in live mode overridden by PermissionContext when ready.
  const [devRole] = useState<DisplayUserRole>(() => {
    const saved = window.localStorage.getItem(USER_ROLE_STORAGE_KEY);
    if (saved && userRoleOptions.some((r) => r.value === saved)) {
      return saved as DisplayUserRole;
    }
    return 'Unify Admin';
  });

  const userRole: DisplayUserRole = getEffectiveDisplayUserRole(rbac, devRole);
  const activeRoleLabel = rbac.ready ? ROLE_LABELS[rbac.activeRole] : userRole;
  const switchableRoles = useMemo(
    () => collapseRolesForDisplay(rbac.availableRoles),
    [rbac.availableRoles],
  );

  // Clear pending-activation tag on first sign-in
  useEffect(() => {
    if (!profile?.id) return;
    const tags = profile.meta?.tag;
    const isPending = tags?.some(
      (t) => t.system === ACCOUNT_STATUS_TAG_SYSTEM && t.code === PENDING_ACTIVATION_TAG_CODE,
    );
    if (!isPending) return;
    const updated = {
      ...profile,
      meta: {
        ...profile.meta,
        tag: tags!.filter(
          (t) => !(t.system === ACCOUNT_STATUS_TAG_SYSTEM && t.code === PENDING_ACTIVATION_TAG_CODE),
        ),
      },
    };
    medplum.updateResource(updated).catch(() => { });
  }, [medplum, profile]);

  const patientsAllowed = userRole !== 'Client' && userRole !== 'Provider';

  const effectivePortalPerms = useMemo(() => {
    return getEffectiveClientPortalPermissions(rbac.permissions, isViewingRelatedClient);
  }, [rbac.permissions, isViewingRelatedClient]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * Client (Patient) users: sidebar only lists client-portal links, but routes like
   * /dashboard/caseload were still reachable by URL. Staff dashboard is not permission-
   * checked server-side in this SPA — Medplum AccessPolicy still limits FHIR data.
   */
  useEffect(() => {
    if (!profile || !rbac.ready || !rbac.isClient) {
      return;
    }
    const p = location.pathname;
    if (!p.startsWith('/dashboard')) {
      return;
    }
    const allowed =
      p === '/dashboard' ||
      p === '/dashboard/' ||
      p.startsWith('/dashboard/settings');
    if (!allowed) {
      navigate(defaultClientPortalHome(effectivePortalPerms), { replace: true });
    }
  }, [profile, rbac.ready, rbac.isClient, effectivePortalPerms, location.pathname, navigate]);

  /** Client portal route guard: redirect from disallowed routes using effective permissions. */
  useEffect(() => {
    if (!rbac.ready || !rbac.isClient) {
      return;
    }
    const path = location.pathname;
    if (!path.startsWith('/client-portal')) {
      return;
    }
    if (isClientPortalPathAllowed(path, effectivePortalPerms) && isClientPortalFeatureVisible(path)) {
      return;
    }
    navigate(defaultClientPortalHome(effectivePortalPerms), { replace: true });
  }, [rbac.ready, rbac.isClient, effectivePortalPerms, location.pathname, navigate]);

  /** Staff FEATURE_VISIBILITY routes stay off hosted Dev/staging/prod even via bookmark. */
  useEffect(() => {
    if (!rbac.ready || rbac.isClient) {
      return;
    }
    if (isStaffFeaturePathVisible(location.pathname, userRole, rbac.role)) {
      return;
    }
    navigate('/dashboard', { replace: true });
  }, [rbac.ready, rbac.isClient, location.pathname, navigate, userRole, rbac.role]);

  const visibleClientPortalNavItems = useMemo(() => {
    if (userRole !== 'Client' || !rbac.ready) {
      return clientPortalNavItems;
    }
    return clientPortalNavItems.filter((item) =>
      clientPortalNavItemAllowed(item.href, effectivePortalPerms)
      && isClientPortalFeatureVisible(item.href),
    );
  }, [userRole, rbac.ready, effectivePortalPerms]);

  const unreadThreadCount = useUnreadThreadCount();
  const withUnreadChatsBadge = useCallback(
    (items: NavItem[]): NavItem[] =>
      items.map((item) =>
        item.href === '/dashboard/messages' || item.href === '/client-portal/messages'
          ? { ...item, badge: unreadThreadCount > 0 ? unreadThreadCount : undefined }
          : item,
      ),
    [unreadThreadCount],
  );

  const inAppNotifications = useInAppNotifications();
  const [notifPopoverOpened, setNotifPopoverOpened] = useState(false);

  // Initialize API client for Spring Boot backend
  useApiInitialization();
  const { status: onboardingStatus, isLoading: onboardingStatusLoading } = useOnboardingStatus();

  // Auth routes (no shell). See `appRouteGate.ts`.
  const isAuthRoute = isAuthPath(location.pathname);
  const isInteractiveUser = isInteractiveUserProfile(profile);

  // Show nothing while Medplum is loading — but never block auth routes.
  // They don't depend on the authenticated profile, and gating them here would
  // unmount SignInPage on every profile refresh.
  if (shouldBlockRenderWhileLoading(medplum.isLoading(), location.pathname)) {
    return <Loading />;
  }

  if (location.pathname === '/') {
    return <Navigate to={isInteractiveUser ? '/dashboard' : '/signin'} replace />;
  }

  const isOnboardingRoute = location.pathname.startsWith('/onboarding');
  if (!isInteractiveUser && !isAuthRoute) {
    return <Navigate to="/signin" replace />;
  }

  // Inconsistent role/profile state → safe access-denied view
  if (isInteractiveUser && rbac.error && !isAuthRoute) {
    return <AccessDenied />;
  }

  // Render onboarding routes (authenticated, minimal shell)
  if (isInteractiveUser && isOnboardingRoute) {
    if (!onboardingStatusLoading && onboardingStatus === 'completed') {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="onboarding" element={<OnboardingLayout />}>
            <Route index element={<OnboardingHubPage />} />
            <Route path="personal-info" element={<PersonalInfoSection />} />
            <Route path="availability" element={<AvailabilitySection />} />
            <Route path="profile" element={<ProfileSection />} />
            <Route path="licenses-education" element={<LicensesEducationSection />} />
            <Route path="attestation" element={<AttestationSection />} />
            <Route path="done" element={<DoneSection />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // Redirect incomplete onboarding to /onboarding for any authenticated app route
  if (
    isInteractiveUser &&
    !onboardingStatusLoading &&
    onboardingStatus &&
    onboardingStatus !== 'completed' &&
    !isAuthRoute &&
    !isOnboardingRoute
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render auth routes without app shell
  if (isAuthRoute) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
          <Route path="/auth/setpassword" element={<SetPasswordAliasRedirect />} />
          <Route path="/set-password/:id/:secret" element={<LegacySetPasswordRedirect />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </Suspense>
    );
  }

  // Render telehealth session fullscreen (no AppShell chrome)
  if (location.pathname.match(/^\/client-portal\/session\//)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/client-portal/session/zoom-call" element={<ZoomCallPage />} />
          <Route path="/client-portal/session/zoom-closed" element={<ZoomClosedPage />} />
          <Route
            path="/client-portal/session/:appointmentId/join"
            element={
              <RequireClientRole>
                <ClientTelehealthJoinPage />
              </RequireClientRole>
            }
          />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.match(/^\/dashboard\/session\//)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/dashboard/session/zoom-call" element={<ZoomCallPage />} />
          <Route path="/dashboard/session/zoom-closed" element={<ZoomClosedPage />} />
          <Route path="/dashboard/session/:appointmentId" element={<TelehealthSessionPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (location.pathname.match(/^\/dashboard\/appointments\/[^/]+\/in-person-recording\/?$/)) {
    return (
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/dashboard/appointments/:appointmentId/in-person-recording"
            element={<InPersonRecordingPage />}
          />
        </Routes>
      </Suspense>
    );
  }

  const handleSignOut = (): void => {
    medplum.signOut();
    navigate('/signin');
  };

  return (
    <ClientsProvider>
      <RecentlyViewedClientsProvider>
      <AiAssistantProvider>
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 250,
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
        padding={
          location.pathname.startsWith('/dashboard/settings') || location.pathname.startsWith('/dashboard/styling')
            ? 0
            : 'md'
        }
        styles={{
          main: {
            backgroundColor: 'var(--unify-bg-warm)',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          },
          header: {
            padding: 0,
            borderBottom: '1px solid #9CA3AF',
          },
          navbar: {
            backgroundColor: 'var(--unify-bg-sidebar)',
            borderRight: '1px solid #9CA3AF',
          },
        }}
      >
        {/* Header: left brand strip + main bar (search, add, bell, user) */}
        <AppShell.Header>
          <Group h="100%" wrap="nowrap" gap={0}>
            {/* Left: sidebar header – on desktop matches sidebar width; on mobile collapses to burger-only */}
            <Group
              h="100%"
              px="md"
              gap="sm"
              wrap="nowrap"
              style={{
                backgroundColor: 'var(--unify-bg-sidebar)',
                borderRight: '1px solid #9CA3AF',
                minWidth: 0,
                width: isMobileViewport ? 'auto' : 250,
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
              <Text size="lg" fw={600} c="#111827" visibleFrom="sm">
                Unify Mental Health
              </Text>
            </Group>

            {/* Right: main header – white; center: search + plus (desktop only); right: bell + user */}
            <Group
              h="100%"
              px="md"
              wrap="nowrap"
              gap={0}
              style={{ flex: 1, backgroundColor: 'var(--unify-bg-header)', minWidth: 0 }}
            >
              <Box style={{ flex: 1, minWidth: 0 }} aria-hidden />
              {/* Search and quick-actions are desktop-only: too wide for mobile header */}
              {userRole !== 'Client' && (
                <Box visibleFrom="sm">
                  <Group gap="xs" wrap="nowrap" align="center">
                    <HudClientSearch />
                    {FEATURE_VISIBILITY.headerQuickAdd ? (
                      <Menu position="bottom-end" width={220} shadow="md">
                        <Menu.Target>
                          <UnstyledButton style={{ display: 'flex', color: '#9CA3AF' }} aria-label="Quick actions">
                            <IconPlus size={20} stroke={1.5} />
                          </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Quick actions</Menu.Label>
                          <Menu.Item
                            leftSection={<IconUserPlus size={16} />}
                            onClick={() => {
                              notifications.show({
                                color: 'blue',
                                title: 'Add new client',
                                message: 'Client intake flow will be available when integrated.',
                              });
                            }}
                          >
                            Add new client
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconCalendarPlus size={16} />}
                            onClick={() => navigate('/dashboard/calendar')}
                          >
                            Add new appointment
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    ) : null}
                  </Group>
                </Box>
              )}
              <Box
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Group gap="sm" wrap="nowrap">
                  <NotificationsPopover
                    opened={notifPopoverOpened}
                    onClose={() => setNotifPopoverOpened(false)}
                    onToggle={() => setNotifPopoverOpened((o) => !o)}
                    notifications={inAppNotifications}
                  />

                  {profile && (
                    <Menu
                      position="bottom-end"
                      width={240}
                      shadow="md"
                      opened={accountMenuOpened}
                      onChange={setAccountMenuOpened}
                    >
                      <Menu.Target>
                        <UnstyledButton aria-label="Account menu">
                          <Avatar
                            radius="xl"
                            size={32}
                            styles={{
                              root: {
                                width: 32,
                                height: 32,
                                minWidth: 32,
                                backgroundColor: 'var(--unify-bg-sidebar)',
                                color: '#374151',
                                border: '1px solid #E5E7EB',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                              },
                            }}
                          >
                            {(() => {
                              const n = profile.name?.[0];
                              const g = n?.given?.[0]?.[0];
                              const f = n?.family?.[0];
                              if (g && f) return `${g}${f}`.toUpperCase();
                              if (n?.text) return n.text.slice(0, 2).toUpperCase();
                              return 'U';
                            })()}
                          </Avatar>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown data-bug-report-exclude="">
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid #E5E7EB' }}>
                          <Text fw={600} size="sm">
                            {profileAccountDisplayName(profile)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {profile.telecom?.find((t) => t.system === 'email')?.value ?? '—'}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {activeRoleLabel}
                          </Text>
                        </div>
                        {userRole !== 'Client' && (
                          <Menu.Item
                            leftSection={<IconUser size={16} />}
                            onClick={() => navigate('/dashboard/settings/profile')}
                          >
                            User Profile
                          </Menu.Item>
                        )}
                        <Menu.Item
                          leftSection={<IconSettings size={16} />}
                          onClick={() => navigate('/dashboard/settings')}
                        >
                          Settings
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconBug size={16} />}
                          onClick={() => {
                            setAccountMenuOpened(false);
                            void bugReport.startReport();
                          }}
                        >
                          Report a bug
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconMessageCircle size={16} />}
                          onClick={() => {
                            setAccountMenuOpened(false);
                            productFeedback.open();
                          }}
                        >
                          Submit feedback
                        </Menu.Item>
                        {rbac.isClient && portalSubject.canSwitch && (
                          <>
                            <Menu.Divider />
                            <Menu.Label>Portal Account</Menu.Label>
                            {portalSubject.availableAccounts.map((account) => (
                              <Menu.Item
                                key={account.patientId}
                                leftSection={<IconSwitchHorizontal size={16} />}
                                rightSection={
                                  portalSubject.activePatientId === account.patientId
                                    ? <IconCheck size={16} stroke={2} style={{ color: '#111827' }} />
                                    : undefined
                                }
                                onClick={() => portalSubject.setActivePatientId(account.patientId)}
                              >
                                {account.label}
                              </Menu.Item>
                            ))}
                          </>
                        )}
                        {switchableRoles.length > 1 && (
                          <>
                            <Menu.Divider />
                            <Menu.Label>Switch Role</Menu.Label>
                            {switchableRoles.map((slug) => (
                              <Menu.Item
                                key={slug}
                                rightSection={
                                  rbac.activeRole === slug
                                    ? <IconCheck size={16} stroke={2} style={{ color: '#111827' }} />
                                    : undefined
                                }
                                onClick={() => {
                                  rbac.setActiveRole(slug);
                                  navigate('/dashboard');
                                }}
                              >
                                {ROLE_LABELS[slug]}
                              </Menu.Item>
                            ))}
                          </>
                        )}
                        <Menu.Item
                          leftSection={<IconLogout size={16} />}
                          color="red"
                          onClick={handleSignOut}
                        >
                          Logout
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Box>
            </Group>
          </Group>
        </AppShell.Header>

        {/* Sidebar Navigation */}
        <AppShell.Navbar p="md">
          <Box mb="sm" px="xs" pb="sm" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" fw={700} tt="uppercase" c="gray.7" lts={0.5}>
              {activeRoleLabel}
            </Text>
          </Box>
          <AppShell.Section grow component={ScrollArea}>
            {(() => {
              const renderItems = (items: NavItem[]): JSX.Element => {
                return (
                  <>
                    {items.map((item) => (
                      <UnifyNavLink
                        key={item.href}
                        href={item.href}
                        label={item.label}
                        icon={<item.icon size={20} stroke={1.5} />}
                        rightSection={item.badge ? <Badge size="xs" variant="filled" circle>{item.badge}</Badge> : undefined}
                        active={isNavActive(item.href, location.pathname)}
                        onNavigate={() => {
                          navigate(item.href);
                          close();
                        }}
                      />
                    ))}
                  </>
                );
              };

              // Client sees only client portal navigation in sidebar (filtered by RBAC permissions).
              if (userRole === 'Client') {
                return renderItems(withUnreadChatsBadge(visibleClientPortalNavItems));
              }

              // Everyone else: Dashboard → Calendar → Caseload, then the rest.
              const calendarNavItem = (
                <UnifyNavLink
                  label="Calendar"
                  icon={<IconCalendarEvent size={20} stroke={1.5} />}
                  href="/dashboard/calendar"
                  active={location.pathname.startsWith('/dashboard/calendar')}
                  onNavigate={() => {
                    navigate('/dashboard/calendar');
                    close();
                  }}
                />
              );

              const caseloadNavItem = (
                <UnifyNavLink
                  label="Caseload"
                  icon={<IconClipboardList size={20} stroke={1.5} />}
                  href="/dashboard/caseload"
                  active={location.pathname.startsWith('/dashboard/caseload') || location.pathname.startsWith('/dashboard/patients')}
                  onNavigate={() => {
                    navigate('/dashboard/caseload');
                    close();
                  }}
                />
              );

              const serviceRequestsNavItem = (FEATURE_VISIBILITY.serviceRequests || userRole === 'Unify Admin') ? (
                <UnifyNavLink
                  label="Service Requests"
                  icon={<IconInbox size={20} stroke={1.5} />}
                  href="/dashboard/episode-tracker"
                  active={location.pathname.startsWith('/dashboard/episode-tracker')}
                  onNavigate={() => {
                    navigate('/dashboard/episode-tracker');
                    close();
                  }}
                />
              ) : null;

              const referralRequestsNavItem = FEATURE_VISIBILITY.referralRequests ? (
                <UnifyNavLink
                  label="Referral Requests"
                  icon={<IconClipboardList size={20} stroke={1.5} />}
                  href="/dashboard/referral-requests"
                  active={location.pathname.startsWith('/dashboard/referral-requests')}
                  onNavigate={() => {
                    navigate('/dashboard/referral-requests');
                    close();
                  }}
                />
              ) : null;

              const billingNavItem = FEATURE_VISIBILITY.staffBilling ? (
                <UnifyNavLink
                  label="Billing"
                  icon={<IconFileInvoice size={20} stroke={1.5} />}
                  href="/dashboard/billing"
                  active={location.pathname.startsWith('/dashboard/billing')}
                  onNavigate={() => {
                    navigate('/dashboard/billing');
                    close();
                  }}
                />
              ) : null;

              const analyticsSubNav = (
                <UnifyNavLink
                  label="Analytics"
                  icon={<IconChartBar size={20} stroke={1.5} />}
                  childrenOffset={28}
                  active={location.pathname.startsWith('/dashboard/analytics')}
                >
                  {[
                    { label: 'Dashboard', href: '/dashboard/analytics/home' },
                    { label: 'Income', href: '/dashboard/analytics/income' },
                    { label: 'Clinical quality', href: '/dashboard/analytics/clinical-quality' },
                    { label: 'Caseload', href: '/dashboard/analytics/caseload' },
                    { label: 'Reports', href: '/dashboard/analytics/reports' },
                  ].map((sub) => (
                    <UnifyNavLink
                      key={sub.href}
                      label={sub.label}
                      href={sub.href}
                      active={location.pathname === sub.href}
                      onNavigate={() => {
                        navigate(sub.href);
                        close();
                      }}
                    />
                  ))}
                </UnifyNavLink>
              );

              const staffCoreNav = (
                <>
                  {renderItems(dashboardNavItems)}
                  {calendarNavItem}
                  {caseloadNavItem}
                  {referralRequestsNavItem}
                  {renderItems(withUnreadChatsBadge(tasksMessagesNavItems))}
                  {serviceRequestsNavItem}
                  {billingNavItem}
                </>
              );

              switch (userRole) {
                case 'Unify Admin':
                  return (
                    <>
                      {staffCoreNav}
                      {FEATURE_VISIBILITY.analytics ? analyticsSubNav : null}
                      {FEATURE_VISIBILITY.enrollments ? renderItems(enrollmentsNavItems) : null}
                      {FEATURE_VISIBILITY.practiceEnrollments ? renderItems(practicesNavItems) : null}
                      {FEATURE_VISIBILITY.groups ? renderItems(groupsNavItems) : null}
                      {FEATURE_VISIBILITY.finances ? renderItems(adminNavItems) : null}
                    </>
                  );
                case 'Provider':
                  return renderItems(withUnreadChatsBadge(providerNavItems));
                case 'Practice Owner':
                case 'Practice Admin':
                  return (
                    <>
                      {staffCoreNav}
                      {FEATURE_VISIBILITY.analytics ? analyticsSubNav : null}
                      {FEATURE_VISIBILITY.enrollments ? renderItems(enrollmentsNavItems) : null}
                      {FEATURE_VISIBILITY.practiceEnrollments ? renderItems(practicesNavItems) : null}
                      {FEATURE_VISIBILITY.groups ? renderItems(groupsNavItems) : null}
                      {FEATURE_VISIBILITY.finances ? renderItems(financesNavItems) : null}
                    </>
                  );
                default:
                  return renderItems(withUnreadChatsBadge(mainNavItems));
              }
            })()}
            {userRole !== 'Client' && (
              <RecentlyViewedNavSection
                onNavigate={(href) => {
                  navigate(href);
                  close();
                }}
              />
            )}
          </AppShell.Section>

          <AppShell.Section>
            {settingsNavItems.map((item) => (
              <UnifyNavLink
                key={item.href}
                href={item.href}
                label={item.label}
                icon={<item.icon size={20} stroke={1.5} />}
                active={isNavActive(item.href, location.pathname)}
                onNavigate={() => {
                  navigate(item.href);
                  close();
                }}
              />
            ))}
          </AppShell.Section>
        </AppShell.Navbar>

        {/* Main Content */}
        <AppShell.Main data-testid="app-shell-main">
          <AiAssistantLayout>
          <Box w="100%" style={{ minWidth: 0 }}>
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    userRole === 'Client' ? (
                      <Navigate to={defaultClientPortalHome(effectivePortalPerms)} replace />
                    ) : (
                      <HomeTilesPage />
                    )
                  }
                />
                <Route path="/dashboard/home" element={<Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard/patients"
                  element={patientsAllowed ? <PatientListPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/patients/:id"
                  element={patientsAllowed ? <PatientDetailPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route path="/dashboard/caseload" element={<ClientsScreen />} />
                <Route
                  path="/dashboard/billing/*"
                  element={
                    FEATURE_VISIBILITY.staffBilling &&
                    (userRole === 'Unify Admin' || userRole === 'Practice Owner' || userRole === 'Practice Admin') ? (
                      <BillingSectionLayout />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                >
                  <Route index element={<BillingIndexPage />} />
                  <Route path="claims" element={<BillingClaimsPage />} />
                  <Route path="ops" element={<OpsQueuePage />} />
                  <Route path="batch-runs" element={<BatchRunsPage />} />
                  <Route path="acknowledgments" element={<AcknowledgmentsPage />} />
                  <Route path="remittances" element={<RemittancesPage />} />
                  <Route path="denials" element={<Navigate to="/dashboard/billing/remittances?outcome=denied" replace />} />
                  <Route path="accounts" element={<AccountsPage />} />
                  <Route path="claims/:claimId" element={<ClaimDetailPage />} />
                  <Route path="claims/:claimId/cms1500" element={<Cms1500ClaimPage />} />
                  <Route path="eobs/:eobId" element={<EobDetailPage />} />
                </Route>
                <Route path="/dashboard/clients/:id" element={<ClientChartPage />} />
                <Route path="/dashboard/clients/:id/profile" element={<ClientProfilePage />} />
                <Route path="/dashboard/clients/:id/episode-of-care/:episodeId" element={<EpisodeOfCarePage />} />
                <Route path="/dashboard/clients/:id/discharge-summary/:summaryId" element={<DischargeSummaryPage />} />
                <Route path="/dashboard/clients/:id/chart-document/new" element={<ChartDocumentPage />} />
                <Route path="/dashboard/clients/:id/chart-document/:responseId" element={<ChartDocumentPage />} />
                <Route path="/dashboard/clients/:id/chart/admin-note" element={<ChartAdminNoteRoute />} />
                <Route path="/dashboard/clients/:id/measures/assessment/:taskId" element={<AssessmentResponsePage />} />
                <Route
                  path="/dashboard/analytics/*"
                  element={
                    FEATURE_VISIBILITY.analytics && (
                      userRole === 'Unify Admin' || userRole === 'Practice Owner' || userRole === 'Practice Admin'
                    ) ? (
                      <AnalyticsRoutes />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route path="/dashboard/tasks" element={<TasksPage />} />
                <Route
                  path="/dashboard/calendar"
                  element={userRole === 'Provider' || userRole === 'Unify Admin' || userRole === 'Practice Owner' || userRole === 'Practice Admin' ? <ProviderCalendarPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/appointments/:appointmentId"
                  element={
                    userRole === 'Client' ? <Navigate to="/dashboard" replace /> : <AppointmentPage />
                  }
                />
                <Route
                  path="/dashboard/trainings"
                  element={
                    FEATURE_VISIBILITY.trainings ? <TrainingsPage /> : <Navigate to="/dashboard" replace />
                  }
                />
                <Route
                  path="/dashboard/enrollments"
                  element={
                    FEATURE_VISIBILITY.enrollments && canAccessEnrollments(rbac.activeRole) ? (
                      <EnrollmentsPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/practices/enrollments"
                  element={
                    FEATURE_VISIBILITY.practiceEnrollments && canSelectEnrollmentPractitioner(rbac.activeRole) ? (
                      <PracticesEnrollmentsPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/practices/enrollments/pdf/:runId"
                  element={
                    FEATURE_VISIBILITY.practiceEnrollments && canSelectEnrollmentPractitioner(rbac.activeRole) ? (
                      <EnrollmentPdfRunPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/groups"
                  element={
                    FEATURE_VISIBILITY.groups &&
                    (userRole === 'Practice Owner' ||
                      userRole === 'Practice Admin' ||
                      userRole === 'Unify Admin') ? (
                      <GroupsPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/groups/:groupId"
                  element={
                    FEATURE_VISIBILITY.groups &&
                    (userRole === 'Practice Owner' ||
                      userRole === 'Practice Admin' ||
                      userRole === 'Unify Admin') ? (
                      <GroupDetailRoute />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route path="/dashboard/supervision" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/cosign-notes" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/unsigned-notes" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/missing-documents" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/compliance" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/meetings" element={userRole === 'Provider' ? <SupervisionDashboardPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/meetings/new" element={userRole === 'Provider' ? <SupervisionMeetingFormPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/meetings/:responseId" element={userRole === 'Provider' ? <SupervisionMeetingFormPage /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/supervision/review/:documentId" element={userRole === 'Provider' ? <DocumentReviewPage /> : <Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard/messages"
                  element={
                    FEATURE_VISIBILITY.staffMessages ? <MessagingPage /> : <Navigate to="/dashboard" replace />
                  }
                />
                <Route
                  path="/dashboard/messages/:threadId"
                  element={
                    FEATURE_VISIBILITY.staffMessages ? <MessagingPage /> : <Navigate to="/dashboard" replace />
                  }
                />
                <Route path="/dashboard/episode-tracker" element={(userRole === 'Unify Admin' || (FEATURE_VISIBILITY.serviceRequests && (userRole === 'Practice Admin' || userRole === 'Practice Owner'))) ? <EpisodeTrackerPage /> : <Navigate to="/dashboard" replace />} />
                <Route
                  path="/dashboard/referral-requests"
                  element={
                    FEATURE_VISIBILITY.referralRequests &&
                    (userRole === 'Unify Admin' || userRole === 'Practice Admin' || userRole === 'Practice Owner') ? (
                      <ReferralRequestsPage />
                    ) : (
                      <Navigate to="/dashboard/caseload" replace />
                    )
                  }
                />
                <Route
                  path="/dashboard/compliance-audit"
                  element={<Navigate to="/dashboard/settings/compliance" replace />}
                />
                <Route
                  path="/dashboard/finances"
                  element={FEATURE_VISIBILITY.finances && (userRole === 'Practice Owner' || userRole === 'Practice Admin') ? (
                    <FinancesPage
                      headerRight={
                        <Tooltip label="Practice finance settings" position="left">
                          <ActionIcon variant="subtle" color="gray" size="lg" onClick={() => navigate('/dashboard/settings/practice?tab=finances')}>
                            <IconSettings size={20} />
                          </ActionIcon>
                        </Tooltip>
                      }
                    />
                  ) : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/finances/invoice/:id"
                  element={FEATURE_VISIBILITY.finances && (userRole === 'Practice Owner' || userRole === 'Practice Admin') ? <InvoiceDetailPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/finances/payment/:id"
                  element={FEATURE_VISIBILITY.finances && (userRole === 'Practice Owner' || userRole === 'Practice Admin') ? <PaymentDetailPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/practice-finances"
                  element={FEATURE_VISIBILITY.finances && userRole === 'Unify Admin' ? <PracticeFinancesPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/practice-finances/invoice/:id"
                  element={FEATURE_VISIBILITY.finances && userRole === 'Unify Admin' ? <InvoiceDetailPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/practice-finances/payment/:id"
                  element={FEATURE_VISIBILITY.finances && userRole === 'Unify Admin' ? <PaymentDetailPage /> : <Navigate to="/dashboard" replace />}
                />
                <Route
                  path="/dashboard/admin/enrollment-pdf"
                  element={<Navigate to={FEATURE_VISIBILITY.practiceEnrollments ? '/dashboard/practices/enrollments?tab=enrollment-pdfs' : '/dashboard'} replace />}
                />
                <Route
                  path="/dashboard/admin/enrollment-pdf/:runId"
                  element={<Navigate to={FEATURE_VISIBILITY.practiceEnrollments ? '/dashboard/practices/enrollments?tab=enrollment-pdfs' : '/dashboard'} replace />}
                />
                <Route path="/dashboard/help" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/settings" element={<MedplumDirectoryUsersLayout />}>
                  <Route index element={<SettingsPage />} />
                  <Route
                    path="documentation/template/new"
                    element={userRole === 'Unify Admin' ? <TemplateEditorPage /> : <Navigate to="/dashboard/settings" replace />}
                  />
                  <Route
                    path="documentation/template/:templateId"
                    element={userRole === 'Unify Admin' ? <TemplateEditorPage /> : <Navigate to="/dashboard/settings" replace />}
                  />
                  <Route path="users/:id" element={<UserSettingsPage />} />
                  <Route path=":section" element={<SettingsPage />} />
                </Route>
                <Route
                  path="/dashboard/styling/*"
                  element={userRole === 'Unify Admin' && FEATURE_VISIBILITY.settingsStylingPlayground ? <DesignSystemRoutes /> : <Navigate to="/dashboard" replace />}
                />
                <Route path="/dashboard/theming" element={userRole === 'Unify Admin' && FEATURE_VISIBILITY.settingsStylingPlayground ? <Navigate to="/dashboard/styling" replace /> : <Navigate to="/dashboard" replace />} />
                <Route path="/dashboard/client-intake" element={<ClientIntakePage />} />
                <Route path="/dashboard/client-intake/directory" element={<ClientIntakeDirectoryPage stickyTop={60} />} />
                <Route
                  path="/client-portal"
                  element={
                    <RequireClientRole>
                      <ClientPortalDashboardPage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/documents"
                  element={
                    FEATURE_VISIBILITY.clientPortalDocuments ? (
                      <RequireClientRole>
                        <ClientPortalDocumentsPage />
                      </RequireClientRole>
                    ) : (
                      <Navigate to="/client-portal" replace />
                    )
                  }
                />
                <Route
                  path="/client-portal/assessments/:taskId"
                  element={
                    <RequireClientRole>
                      <AssessmentFillPage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/billing"
                  element={
                    <RequireClientRole>
                      <ClientPortalBillingPage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/messages/:threadId"
                  element={
                    FEATURE_VISIBILITY.clientPortalMessages ? (
                      <RequireClientRole>
                        <ClientPortalMessagesPage />
                      </RequireClientRole>
                    ) : (
                      <Navigate to="/client-portal" replace />
                    )
                  }
                />
                <Route
                  path="/client-portal/messages"
                  element={
                    FEATURE_VISIBILITY.clientPortalMessages ? (
                      <RequireClientRole>
                        <ClientPortalMessagesPage />
                      </RequireClientRole>
                    ) : (
                      <Navigate to="/client-portal" replace />
                    )
                  }
                />
                <Route
                  path="/client-portal/directory"
                  element={
                    <RequireClientRole>
                      <ClientPortalDirectoryPage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/intake"
                  element={
                    <RequireClientRole>
                      <ClientPortalIntakePage />
                    </RequireClientRole>
                  }
                />
                <Route
                  path="/client-portal/request-service"
                  element={
                    FEATURE_VISIBILITY.clientPortalPendingServiceRequests ? (
                      <RequireClientRole>
                        <ClientPortalRequestServicePage />
                      </RequireClientRole>
                    ) : (
                      <Navigate to="/client-portal" replace />
                    )
                  }
                />
                <Route
                  path="/client-portal/requests"
                  element={
                    FEATURE_VISIBILITY.clientPortalPendingServiceRequests ? (
                      <RequireClientRole>
                        <ClientPortalRequestStatusPage />
                      </RequireClientRole>
                    ) : (
                      <Navigate to="/client-portal" replace />
                    )
                  }
                />
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route
                  path="*"
                  element={
                    <Navigate
                      to={userRole === 'Client' ? defaultClientPortalHome(effectivePortalPerms) : '/dashboard'}
                      replace
                    />
                  }
                />
              </Routes>
            </Suspense>
          </Box >
          </AiAssistantLayout>
        </AppShell.Main >
        <FreudLauncher visible={userRole !== 'Client'} />
      </AppShell>
      <BugReportModal
        opened={bugReport.opened}
        capturing={bugReport.capturing}
        screenshotBase64={bugReport.screenshotBase64}
        submitting={bugReport.submitting}
        onClose={bugReport.onClose}
        onSubmit={(values) => void bugReport.onSubmit(values)}
        onScreenshotChange={bugReport.onScreenshotChange}
      />
      <FeedbackModal
        opened={productFeedback.opened}
        submitting={productFeedback.submitting}
        onClose={productFeedback.onClose}
        onSubmit={(values) => void productFeedback.onSubmit(values)}
      />
      </AiAssistantProvider>
      </RecentlyViewedClientsProvider>
    </ClientsProvider>
  );
}